import { joinWords, type ImportEntity, type ImportResult } from '@quire/shared';

/**
 * The words on the import panel, kept apart from the component so the
 * sentence somebody reads before pressing Import can be pinned by a unit test
 * at 0, at 1 and at many. Every count clause that would read "and enrols 0"
 * is dropped, and the tense turns once the import has happened.
 */

export interface ImportEntityCopy {
  noun: string;
  plural: string;
  /** Two or three sentences: the columns, what is required, the order of files. */
  intro: string;
}

export const IMPORT_COPY: Record<ImportEntity, ImportEntityCopy> = {
  periods: {
    noun: 'period',
    plural: 'periods',
    intro:
      'A CSV with the columns name, start_time and end_time; only name is required. ' +
      'Import periods first, then classes, then students, so each file can name what the one ' +
      'before it created.',
  },
  classes: {
    noun: 'class',
    plural: 'classes',
    intro:
      'A CSV with the columns name, periods and teachers; only name is required. periods holds ' +
      'period names and teachers holds staff emails, each separated by semicolons, and both must ' +
      'exist already. Import periods first, then classes, then students.',
  },
  students: {
    noun: 'student',
    plural: 'students',
    intro:
      'A CSV with the columns first_name, last_name, date_of_birth, guardian_name, ' +
      'guardian_contact and class; only the two names are required. class holds one or more ' +
      'class names separated by semicolons, and they must exist already. Import periods first, ' +
      'then classes, then students.',
  },
};

/** `no students`, `1 student`, `3 students`. */
export function count(n: number, one: string, many: string): string {
  if (n === 0) return `no ${many}`;
  return `${n} ${n === 1 ? one : many}`;
}

/** Links a file would write, over and above the records it creates. */
function links(result: ImportResult): number {
  switch (result.entity) {
    case 'periods':
      return 0;
    case 'classes':
      return result.attached + result.assigned;
    case 'students':
      return result.enrolled;
  }
}

function alreadyThere(result: ImportResult): string {
  const n = result.existing;
  const past = result.committed;
  if (result.entity === 'students') {
    const verb = past ? (n === 1 ? 'was' : 'were') : n === 1 ? 'is' : 'are';
    return `${n} ${verb} already on the roll.`;
  }
  return `${n} already ${past ? 'existed' : n === 1 ? 'exists' : 'exist'}.`;
}

function nothingToDo(result: ImportResult): string {
  const { noun, plural } = IMPORT_COPY[result.entity];
  const n = result.existing;
  if (result.entity === 'students') {
    return n === 1
      ? `Nothing to import: that ${noun} is already on the roll.`
      : `Nothing to import: all ${n} ${plural} are already on the roll.`;
  }
  return n === 1
    ? `Nothing to import: that ${noun} already exists.`
    : `Nothing to import: all ${n} ${plural} already exist.`;
}

/** The one sentence (or two) above the button: present tense before, past tense after. */
export function describeImport(result: ImportResult): string {
  const { noun, plural } = IMPORT_COPY[result.entity];
  if (result.created === 0 && links(result) === 0) return nothingToDo(result);

  const past = result.committed;
  const clauses = [`${past ? 'Added' : 'Adds'} ${count(result.created, noun, plural)}`];
  if (result.entity === 'classes') {
    if (result.attached > 0) {
      clauses.push(
        `${past ? 'attached' : 'attaches'} ${count(result.attached, 'period', 'periods')}`,
      );
    }
    if (result.assigned > 0) {
      clauses.push(
        `${past ? 'assigned' : 'assigns'} ${count(result.assigned, 'teacher', 'teachers')}`,
      );
    }
  } else if (result.entity === 'students' && result.enrolled > 0) {
    clauses.push(`${past ? 'enrolled' : 'enrols'} ${result.enrolled}`);
  }

  const lead = `${joinWords(clauses)}.`;
  return result.existing > 0 ? `${lead} ${alreadyThere(result)}` : lead;
}

/** The button's label, or null when there is nothing for it to do. */
export function importAction(result: ImportResult): string | null {
  if (result.problems.length > 0) return null;
  const { noun, plural } = IMPORT_COPY[result.entity];
  if (result.created > 0) return `Import ${count(result.created, noun, plural)}`;
  return links(result) > 0 ? 'Import' : null;
}

export function problemsIntro(n: number): string {
  return `Nothing was imported. Fix ${n === 1 ? 'this' : 'these'} and check again.`;
}
